"use client";

import Link from "next/link";
import { capture } from "@/lib/analytics/events";
import { ClaimForm } from "@/components/OrgReviewForms";
import type { FormResult } from "@/lib/org-reviews";

/**
 * "Work at X?" CTA on org pages, aimed at the org's own staff landing here
 * (search, a shared link, a candidate forwarding it). Two routes: claim the
 * page (respond to reviews, keep the profile accurate) or /post-a-job, the
 * real $99/$149 checkout entry point.
 */
export function OrgClaimBanner({
  orgKey,
  label,
  liveJobs,
  claimAction,
}: {
  orgKey: string;
  label: string;
  /** Org page only — lets the banner state the free distribution already happening. */
  liveJobs?: number;
  /** Org page only. Without it (job page sidebar) this is the plain post-a-job CTA. */
  claimAction?: (fd: FormData) => Promise<FormResult>;
}) {
  const postLink = (text: string) => (
    <Link
      href="/post-a-job"
      onClick={() => capture("org_claim_cta_click", { org_key: orgKey })}
      className="font-semibold text-brand-700 underline-offset-2 hover:underline"
    >
      {text}
    </Link>
  );
  // Keep "free" and "vacanc(y|ies)" on the same source line — the
  // no-free-claims.test.ts guard reads this file line by line and only
  // exempts a "free" claim when it can see the truly-free word right there.
  const freeDistributionNote =
    liveJobs && liveJobs > 0 ? (
      <span className="font-semibold text-ink">
        We already list {liveJobs} open vacanc{liveJobs === 1 ? "y" : "ies"} from {label} here, free, and route every applicant straight to your official listing.{" "}
      </span>
    ) : null;
  return (
    <div className="mt-6 rounded-lg border border-brand-100 bg-brand-50/50 px-4 py-3">
      {claimAction ? (
        <>
          <p className="text-[13px] leading-relaxed text-neutral-700">
            {freeDistributionNote}
            <span className="font-semibold text-ink">Work at {label}?</span>{" "}
            Claim this page to respond to reviews and keep the profile accurate, or{" "}
            {postLink("post a job")} so candidates browsing this page see it first.
          </p>
          <div className="mt-1 text-[13px]">
            <ClaimForm orgLabel={label} action={claimAction} />
          </div>
        </>
      ) : (
        <p className="text-[13px] leading-relaxed text-neutral-700">
          <span className="font-semibold text-ink">Work at {label}?</span>{" "}
          Post your team&rsquo;s open roles here so candidates already browsing
          this page see them first. {postLink("Post a job →")}
        </p>
      )}
    </div>
  );
}
